import { useState } from 'react';
import { useConditionalDomainQuery } from '#api/domains.queries.ts';
import { DetailCard } from '#components/atoms/cards/detail-card/DetailCard.tsx';
import { OIDCIcon } from '#components/atoms/icons/OIDCIcon.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { DetailList } from '#components/patterns/detail-list/DetailList.tsx';
import { DetailListRow } from '#components/patterns/detail-list/DetailListRow.tsx';
import { NetworkLink } from '#components/patterns/entity-links/network-link/NetworkLink.tsx';
import { MeatballMenu, MenuItem } from '#components/patterns/menus/meatball-menu/index.ts';
import type { EndpointOIDC, EndpointOIDCStatus } from '#dn-types/endpoint-oidc.ts';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { DeleteEndpointIdentityProviderDialog } from '../../delete/endpoint/DeleteEndpointIdentityProviderDialog.tsx';
import { MoveDomainDialog } from '../../list/domains/MoveDomainDialog.tsx';
import { convertHoursToFriendlyUnits, makeAuthValidityString } from '../../util/convertHoursToFriendlyUnits.ts';
import { useToggleEnabled } from './EndpointOIDCProviderCard.queries.ts';
import styles from './EndpointOIDCProviderCard.module.css';

const STATUS_LABELS: Record<EndpointOIDCStatus, string> = {
  enabled: 'Enabled',
  disabled: 'Disabled',
  'no domain': 'No domain assigned',
  'pending verification': 'Pending verification',
};

type Props = {
  endpointOIDC: EndpointOIDC;
  setGlobalErrorMsg: (msg: string | null) => void;
};

export function EndpointOIDCProviderCard({ endpointOIDC, setGlobalErrorMsg }: Props) {
  const { data: { data: domain } = {} } = useConditionalDomainQuery(endpointOIDC.domainID);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [conflictingProvider, setConflictingProvider] = useState<EndpointOIDC | null>(null);
  const location = useAppLocation();

  const [toggleEnabled, { isPending }] = useToggleEnabled(endpointOIDC, {
    onError: setGlobalErrorMsg,
    onConflict: setConflictingProvider,
  });

  let status: EndpointOIDCStatus = endpointOIDC.enabled ? 'enabled' : 'disabled';
  if (!domain) {
    status = 'no domain';
  } else if (!domain.isVerified) {
    status = 'pending verification';
  }

  return (
    <>
      <DetailCard>
        <div className={styles.EndpointOIDCProviderCard_top}>
          <div className="flex">
            <OIDCIcon status={status} />
            <div className={styles.EndpointOIDCProviderCard_title}>
              <p className="h4 mb-4 wrap-anywhere">{`${endpointOIDC.name}${domain ? ` / ${domain.domainName}` : ''}`}</p>
              <p className="p-md m-0">{STATUS_LABELS[status]}</p>
            </div>
          </div>
          <MeatballMenu aria-label="Modify provider" className={styles.EndpointOIDCProviderCard_menu}>
            {(menu) => (
              <>
                <MenuItem render={<Link to={endpointOIDC.id} state={{ from: location }} />}>Edit</MenuItem>
                {domain?.isVerified ?
                  <MenuItem onClick={() => toggleEnabled(menu)} hideOnClick={false} disabled={isPending}>
                    {status !== 'disabled' ? 'Disable' : 'Enable'}
                  </MenuItem>
                : domain ?
                  <MenuItem render={<Link to={`/settings/sso/domains/${domain.id}/verify`} />}>Verify domain</MenuItem>
                : null}
                <MenuItem onClick={() => setShowDeletionModal(true)}>Delete</MenuItem>
              </>
            )}
          </MeatballMenu>
        </div>

        <div className={styles.EndpointOIDCProviderCard_info}>
          <DetailList>
            <DetailListRow label="Network" value={<NetworkLink networkId={endpointOIDC.networkID} />} />
            <DetailListRow
              label="Auth validity"
              value={makeAuthValidityString(...convertHoursToFriendlyUnits(endpointOIDC.authValidityHours))}
            />
          </DetailList>
          <DetailList>
            <DetailListRow label="Issuer URL" value={endpointOIDC.issuerURL} />
            <DetailListRow label="Client ID" value={endpointOIDC.clientID} />
          </DetailList>
        </div>
      </DetailCard>
      {showDeletionModal ?
        <DeleteEndpointIdentityProviderDialog
          endpointOIDC={endpointOIDC}
          onClose={() => {
            setShowDeletionModal(false);
          }}
        />
      : null}
      {conflictingProvider ?
        <MoveDomainDialog
          fromEndpointOIDC={conflictingProvider}
          toEndpointOIDC={endpointOIDC}
          domainName={domain?.domainName ?? 'this domain'} // domainName will be defined, otherwise we wouldn't have a conflict.  But this makes TS happy
          onCancel={() => {
            setConflictingProvider(null);
          }}
          onSuccess={() => {
            setConflictingProvider(null);
          }}
        />
      : null}
    </>
  );
}
